import { Create, useForm, useSelect } from "@refinedev/antd";
import { DatePicker, Form, Input, Select, Space, Tag } from "antd";
import { IResourceComponentsProps } from "@refinedev/core";
import { CloudServerOutlined, DollarOutlined, SettingOutlined, DesktopOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import { IMetadataAdp, ISubscription } from "interfaces";
import { FormSection, FormFieldGrid } from "components/form";
import { PricingFields, buildAvcsFlatFeeRules } from "components/metadata/PricingFields";
import { useAvcsForSubscription } from "components/metadata/useAvcsForSubscription";
import {
  defaultEnrollmentDate,
  enrollmentDateFieldProps,
} from "components/metadata/enrollmentDate";

export const MetadataAdpCreate: React.FC<IResourceComponentsProps> = () => {
  const {
    formProps,
    saveButtonProps,
    formLoading: isLoading,
  } = useForm<IMetadataAdp>({ redirect: "list" });

  const { selectProps } = useSelect<ISubscription>({
    resource: "subscription",
    optionLabel: "subscriptionName",
    optionValue: "id",

    pagination: {
      mode: "server"
    }
  });

  const selectedSubscriptionId = Form.useWatch<string | undefined>(
    ["subscription", "id"],
    formProps.form,
  );

  const {
    amount: avcsAmount,
    currency: avcsCurrency,
    subscriptionType: avcsSubscriptionType,
  } = useAvcsForSubscription(selectedSubscriptionId);

  const avcsFlatFeeRules = buildAvcsFlatFeeRules({
    avcsAmount,
    avcsSubscriptionType,
    enabled: true,
  });

  return (
    <Create
      breadcrumb={false}
      saveButtonProps={saveButtonProps}
      headerProps={{
        onBack: () => window.history.back(),
        title: "Create ADP Service",
        subTitle: "setup new ADP service",
        tags: isLoading ? (
          <Tag color="orange">loading</Tag>
        ) : (
          <Tag color="green">live</Tag>
        ),
      }}
    >
      <Form
        {...formProps}
        layout="vertical"
        initialValues={{
          productName: "ADP",
          productGroup: "ADP",
          adllPublication: "enabled",
          adrsPublication: "enabled",
          totalTidePublication: "enabled",
          currency: "GBP",
          discountPercentage: 0,
          annualFlatFeePerVessel: 0,
          paymentFrequency: "Annual",
          subscriptionType: "Flat Fee Service - Worldwide",
          licenceType: "full",
          activationStatus: "active",
          expiryDate: dayjs(new Date()).add(1, "year"),
          enrollmentDate: defaultEnrollmentDate(),
        }}
      >
        <Space direction="vertical" size={24} style={{ width: "100%" }}>
          <FormSection
            title="Service Configuration"
            subtitle="Product and subscription settings"
            icon={<CloudServerOutlined />}
            variant="elevated"
          >
            <FormFieldGrid columns={2}>
              <Form.Item
                label="Subscription"
                name={["subscription", "id"]}
                rules={[{ required: true }]}
              >
                <Select {...selectProps} />
              </Form.Item>
              <Form.Item
                label="Product Name"
                name="productName"
                rules={[{ required: true }]}
              >
                <Select
                  options={[
                    { label: "AVCSCharts", value: "AVCSCharts" },
                    { label: "ARCSCharts", value: "ARCSCharts" },
                    { label: "ADP", value: "ADP" },
                    { label: "ENP", value: "ENP" },
                  ]}
                />
              </Form.Item>
              <Form.Item
                label="Product Group"
                name="productGroup"
                rules={[{ required: false }]}
              >
                <Select
                  options={[
                    { label: "AVCS Products", value: "AVCS Products" },
                    { label: "ARCS Products", value: "ARCS Products" },
                    { label: "ADP", value: "ADP" },
                    { label: "e-NPs", value: "e-NPs" },
                  ]}
                />
              </Form.Item>
              <Form.Item
                label="ADLL Publication"
                name="adllPublication"
                rules={[{ required: false }]}
              >
                <Select
                  options={[
                    { label: "enabled", value: "enabled" },
                    { label: "not enabled", value: "not enabled" },
                  ]}
                />
              </Form.Item>
              <Form.Item
                label="ADRS Publication"
                name="adrsPublication"
                rules={[{ required: false }]}
              >
                <Select
                  options={[
                    { label: "enabled", value: "enabled" },
                    { label: "not enabled", value: "not enabled" },
                  ]}
                />
              </Form.Item>
              <Form.Item
                label="Total Tide Publication"
                name="totalTidePublication"
                rules={[{ required: false }]}
              >
                <Select
                  options={[
                    { label: "enabled", value: "enabled" },
                    { label: "not enabled", value: "not enabled" },
                  ]}
                />
              </Form.Item>
            </FormFieldGrid>
          </FormSection>

          <FormSection
            title="Billing Information"
            subtitle="Pricing and payment configuration"
            icon={<DollarOutlined />}
            variant="elevated"
          >
            <FormFieldGrid columns={2}>
              <Form.Item
                label="Subscription Type"
                name="subscriptionType"
                rules={[{ required: true }, ...avcsFlatFeeRules]}
                validateTrigger={["onChange", "onBlur"]}
              >
                <Select
                  options={[
                    { label: "Pay Per Use - Quotation", value: "Pay Per Use - Quotation" },
                    { label: "Flat Fee Service - Worldwide", value: "Flat Fee Service - Worldwide" },
                    { label: "Flat Fee Service - Fixed Run", value: "Flat Fee Service - Fixed Run" },
                  ]}
                />
              </Form.Item>
              <Form.Item
                label="Payment Frequency"
                name="paymentFrequency"
                rules={[{ required: true }]}
              >
                <Select
                  options={[
                    { label: "Annual", value: "Annual" },
                    { label: "Semi-Annual", value: "Semi-Annual" },
                    { label: "Quarterly", value: "Quarterly" },
                    { label: "Monthly", value: "Monthly" },
                  ]}
                />
              </Form.Item>
              <PricingFields
                form={formProps.form}
                avcsFlatFeeAmount={avcsAmount}
                avcsFlatFeeCurrency={avcsCurrency}
                avcsSubscriptionType={avcsSubscriptionType}
                lockCurrency
                errorMode="block"
              />
            </FormFieldGrid>
          </FormSection>

          <FormSection
            title="Licence Details"
            subtitle="Activation and expiry settings"
            icon={<SettingOutlined />}
            variant="elevated"
          >
            <FormFieldGrid columns={2}>
              <Form.Item
                label="Licence Type"
                name="licenceType"
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label="Activation Status"
                name="activationStatus"
                rules={[{ required: false }]}
              >
                <Select
                  options={[
                    { label: "active", value: "active" },
                    { label: "inactive", value: "inactive" },
                  ]}
                />
              </Form.Item>
              <Form.Item
                label="Enrollment Date"
                name="enrollmentDate"
                rules={[{ required: true }]}
                {...enrollmentDateFieldProps}
              >
                <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
              </Form.Item>
              <Form.Item
                label="Expiry Date"
                name="expiryDate"
                rules={[{ required: true }]}
                getValueProps={(value) => ({
                  value: value ? dayjs(value) : "",
                })}
              >
                <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
              </Form.Item>
              <Form.Item
                label="TID"
                name="tid"
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label="Startup Key"
                name="startupKey"
                rules={[{ required: false }]}
              >
                <Input />
              </Form.Item>
            </FormFieldGrid>
          </FormSection>

          <FormSection
            title="Master Machine Configuration"
            subtitle="Primary system details"
            icon={<DesktopOutlined />}
          >
            <FormFieldGrid columns={2}>
              <Form.Item
                label="Master Machine Name"
                name="masterMachineName"
                rules={[{ required: false }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label="Master Machine ID"
                name="masterMachineId"
                rules={[{ required: false }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label="Master Machine Key"
                name="masterMachineKey"
                rules={[{ required: false }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label="Master Machine Version"
                name="masterMachineVersion"
                rules={[{ required: false }]}
              >
                <Input />
              </Form.Item>
            </FormFieldGrid>
          </FormSection>

          <FormSection
            title="Backup Machine Configuration"
            subtitle="Backup system details"
            icon={<DesktopOutlined />}
            variant="subtle"
          >
            <FormFieldGrid columns={2}>
              <Form.Item
                label="Backup Machine Name"
                name="backupMachineName"
                rules={[{ required: false }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label="Backup Machine ID"
                name="backupMachineId"
                rules={[{ required: false }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label="Backup Machine Key"
                name="backupMachineKey"
                rules={[{ required: false }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label="Backup Machine Version"
                name="backupMachineVersion"
                rules={[{ required: false }]}
              >
                <Input />
              </Form.Item>
            </FormFieldGrid>
          </FormSection>
        </Space>
      </Form>
    </Create>
  );
};
